import { images } from './images';
import type { Destination } from '../types/content';

const standardFacilities = [
  'Classrooms with interactive screens',
  'Student lounge',
  'Self-study area',
  'Free Wi-Fi',
  'Student services desk',
];

const sampleDates = ['00 Month – 00 Month 0000', '00 Month – 00 Month 0000'];

function seasonalCopy(name: string, slugKeyword: string, extra: string) {
  return {
    keyword: `english summer camp ${slugKeyword}`,
    metaTitle: `English Summer Camp in ${name} | Seasonal Programmes | ES World`,
    metaDescription: `Join an English summer camp in ${name}. Lessons, activities and safe care for young learners. See seasonal dates.`,
    h1: `English summer camp and seasonal programmes in ${name}`,
    lead: `Join an English summer camp or a seasonal programme in ${name}. Students take English lessons in the morning and join activities and trips in the afternoon. ${extra} Group leaders care for young learners all day, and our team supports families before and during the stay.`,
    dates: sampleDates,
  };
}

export const destinations: Destination[] = [
  {
    slug: 'dubai',
    name: 'Dubai',
    country: 'United Arab Emirates',
    yearRound: {
      keyword: 'english courses in dubai',
      metaTitle: 'English Courses in Dubai | JLT and JBR Campuses | ES World',
      metaDescription: 'Take English courses in Dubai at our JLT and JBR campuses. Classes start every Monday. See Dubai prices and contact us.',
      h1: 'English courses in Dubai, all year',
      lead: 'Study English at our two Dubai campuses in Jumeirah Lake Towers (JLT) and Jumeirah Beach Residence (JBR), minutes from Dubai Marina, the beach, malls and the metro. Over 9,000 students a year from 106+ nationalities learn here, with smart classrooms, an average class size of 16 and student support in more than 20 languages.',
    },
    seasonal: seasonalCopy('Dubai', 'dubai', 'Seasonal programmes include camps, mini-stays for school groups and short courses.'),
    image: images.dubai,
    campuses: [
      { name: 'Mazaya campus, JLT', lines: ['15th floor, BB1, Mazaya Business Avenue', 'Jumeirah Lake Towers, Dubai, UAE', '+971 4 398 2815 · +971 50 906 5836'] },
      { name: 'The Beach campus, JBR', lines: ['Jumeirah Beach Residence', 'Dubai, UAE'] },
    ],
    facilities: [
      '34 classrooms with interactive LED screens',
      'Library and self-study area',
      'Student lounge and coffee shop',
      'Average class size of 16',
      'Morning, midday and late-afternoon timetables',
      'Student support in 20+ languages',
      'Dedicated careers office with job board and CV workshops',
      'Free Wi-Fi',
    ],
    transport: 'Both campuses sit close to Dubai Marina, JBR beach and the metro and tram network, so it is easy to get around the city by public transport.',
    activities: [
      { name: 'Desert safari', text: 'Ride over the dunes and share an evening meal under the stars.' },
      { name: 'Burj Khalifa', text: 'See the whole city from the world’s tallest building.' },
      { name: 'Ferrari World', text: 'Spend a day of rides and thrills on Yas Island.' },
      { name: 'Old Town walk', text: 'Explore historic streets, souks and the creek.' },
      { name: 'Dubai Frame', text: 'See old and new Dubai from one viewing point.' },
      { name: 'The View at The Palm', text: 'Look over the island and the coast from a viewing deck.' },
      { name: 'Abu Dhabi tour', text: 'Visit the Grand Mosque, museums and the waterfront.' },
      { name: 'Musandam tour', text: 'A day on a boat among sea and mountain views.' },
    ],
    visaNote: 'ES World sponsors 1-year and 6-month student residence visas for courses longer than 6 months, with a 90%+ approval rate. A residence visa gives you an Emirates ID, a bank account, a mobile plan and multiple entry. Short courses can use a 30- or 60-day tourist visa, extendable up to 120 days. Our admissions officer starts your visa once your course is booked and paid.',
    priceList: 'dubai',
  },
  {
    slug: 'london',
    name: 'London',
    country: 'United Kingdom',
    yearRound: {
      keyword: 'study english in london',
      metaTitle: 'Study English in London | English Courses in the UK | ES World',
      metaDescription: 'Study English in London with general, exam, business and professional courses. Start any Monday. See London prices.',
      h1: 'Study English in London, all year',
      lead: 'Study English in Bloomsbury, central London, at our campus on Woburn Square — a short walk from King’s Cross and the Tube. Choose General English, IELTS Preparation, the Speaking Class or Business English, with an average class size of 14, CELTA-qualified teachers and student support in more than 20 languages.',
    },
    seasonal: seasonalCopy('London', 'london', 'Seasonal programmes include camps, academic camps, mini-stays and short courses.'),
    image: images.london,
    campuses: [{ name: 'Bloomsbury campus', lines: ['27–28 Woburn Square', 'London WC1H 0AA, United Kingdom', '+44 7490 342009'] }],
    facilities: [
      '14 classrooms with interactive LED screens',
      'Library, lounge, coffee shop and garden',
      'Average class size of 14',
      'Morning, midday and late-afternoon timetables',
      'Student portal for tests, progress and certificates',
      'Student support in 20+ languages',
      'Free Wi-Fi',
    ],
    transport: 'The campus is in Bloomsbury, a short walk from King’s Cross and several Tube stations. Our team shows you how to buy a travel card in your first week.',
    activities: [
      { name: 'London Eye', text: 'Ride high above the Thames for views across the city.' },
      { name: 'Buckingham Palace', text: 'See the King’s London home and the Changing of the Guard.' },
      { name: 'Tower Bridge', text: 'Walk London’s most famous bridge and the riverside.' },
      { name: 'British Museum', text: 'Explore world history, a few minutes from campus.' },
      { name: 'Sky Garden', text: 'Enjoy free panoramic views from a rooftop garden.' },
      { name: 'Windsor Castle', text: 'Take a day trip to the oldest royal castle still in use.' },
      { name: 'St Paul’s Cathedral', text: 'Visit one of London’s great landmarks.' },
      { name: 'The Shard', text: 'See the skyline from Western Europe’s tallest building.' },
    ],
    visaNote: 'Visa rules for the UK depend on your nationality and course length. Our admissions team explains the options for you.',
    priceList: 'london',
  },
  {
    slug: 'oxfordshire',
    name: 'Oxfordshire',
    country: 'United Kingdom',
    seasonal: seasonalCopy('Oxfordshire', 'oxfordshire', 'Students stay on a residential campus with green spaces, sports areas and historic buildings.'),
    image: images.oxfordshire,
    campuses: [{ name: 'Oxfordshire summer campus', lines: ['Sample College, 000 Sample Road', 'Oxfordshire AB0 0CD, United Kingdom'] }],
    facilities: ['Residential rooms on campus', 'Dining hall', 'Sports fields', 'Classrooms', 'Free Wi-Fi'],
    transport: 'Group leaders travel with students on every trip. Airport transfers run on set arrival days.',
    activities: [
      { name: 'Historic city tour', text: 'Walk past old colleges, libraries and gardens.' },
      { name: 'Punting on the river', text: 'Try a traditional flat boat ride with a guide.' },
      { name: 'Day trip to London', text: 'Visit famous sights with your group leaders.' },
      { name: 'Sports afternoon', text: 'Play football, tennis or team games on campus.' },
      { name: 'Talent show', text: 'Sing, dance or act in English with new friends.' },
    ],
    visaNote: 'Visa rules for the UK depend on your nationality. Our team explains the options for each student.',
  },
  {
    slug: 'new-york',
    name: 'New York',
    country: 'United States',
    seasonal: seasonalCopy('New York', 'new york', 'Seasonal programmes include camps, high school programmes and short courses.'),
    image: images.newYork,
    campuses: [{ name: 'New York summer centre', lines: ['000 Sample Avenue, Floor 00', 'New York, NY 00000, USA'] }],
    facilities: standardFacilities,
    transport: 'The centre is a short walk from several subway lines. Group leaders travel with young learners.',
    activities: [
      { name: 'Art museum visit', text: 'Explore a large art collection with a student guide.' },
      { name: 'Park walk and picnic', text: 'Meet classmates in a city park on a sunny afternoon.' },
      { name: 'Food market tour', text: 'Taste food from many cultures in one market.' },
      { name: 'Live music evening', text: 'Listen to a local band and practise English after the show.' },
      { name: 'Day trip to the coast', text: 'Spend a day at the beach by train.' },
    ],
    visaNote: 'Visa rules for the USA depend on your nationality and programme length. Our admissions team explains each step.',
  },
  {
    slug: 'new-haven',
    name: 'New Haven',
    country: 'United States',
    seasonal: seasonalCopy('New Haven', 'new haven', 'Students stay on a campus close to the city centre and the train station.'),
    image: images.newHaven,
    campuses: [{ name: 'New Haven summer campus', lines: ['000 Sample Street', 'New Haven, CT 00000, USA'] }],
    facilities: ['Residential rooms', 'Dining hall', 'Classrooms', 'Sports areas', 'Free Wi-Fi'],
    transport: 'The campus is a short walk from the city centre. Trains connect the city to other places on the east coast.',
    activities: [
      { name: 'Historic walking tour', text: 'Walk past old buildings and learn about local history.' },
      { name: 'Art gallery visit', text: 'Visit a gallery and describe your favourite work in English.' },
      { name: 'Day trip by train', text: 'Travel with your group to a nearby large city.' },
      { name: 'Beach afternoon', text: 'Relax by the sea and play team games.' },
      { name: 'Pizza evening', text: 'Try a local food tradition with your classmates.' },
    ],
    visaNote: 'Visa rules for the USA depend on your nationality and programme length. Our admissions team explains each step.',
  },
  {
    slug: 'los-angeles',
    name: 'Los Angeles',
    country: 'United States',
    seasonal: seasonalCopy('Los Angeles', 'los angeles', 'Seasonal programmes include camps, academic camps and short courses.'),
    image: images.losAngeles,
    campuses: [{ name: 'Los Angeles summer campus', lines: ['000 Sample Boulevard, Suite 000', 'Los Angeles, CA 00000, USA'] }],
    facilities: standardFacilities,
    transport: 'Group leaders plan every journey for young learners. Bus and metro lines stop near the campus.',
    activities: [
      { name: 'Beach afternoon', text: 'Relax at the beach and play team games with classmates.' },
      { name: 'Hiking trail', text: 'Walk a short trail with views over the city.' },
      { name: 'Art museum visit', text: 'See modern art and talk about it in English.' },
      { name: 'Food truck evening', text: 'Try street food from many countries.' },
      { name: 'Film evening', text: 'Watch a film in English and discuss it in a group.' },
    ],
    visaNote: 'Visa rules for the USA depend on your nationality and programme length. Our admissions team explains each step.',
  },
  {
    slug: 'tokyo',
    name: 'Tokyo',
    country: 'Japan',
    seasonal: seasonalCopy('Tokyo', 'tokyo', 'Seasonal programmes include camps, mini-stays for school groups and short courses.'),
    image: images.tokyo,
    campuses: [{ name: 'Tokyo summer centre', lines: ['0-0-0 Sample, Floor 00', 'Tokyo 000-0000, Japan'] }],
    facilities: standardFacilities,
    transport: 'The centre is a short walk from train and subway stations.',
    activities: [
      { name: 'Temple visit', text: 'Visit a historic temple and learn about its traditions.' },
      { name: 'Food market tour', text: 'Taste local food and learn the English words for it.' },
      { name: 'Karaoke evening', text: 'Sing in English with your classmates.' },
      { name: 'Garden walk', text: 'Walk through a traditional garden with a guide.' },
      { name: 'Day trip to the mountains', text: 'Travel by train to a lake with mountain views.' },
    ],
    visaNote: 'Visa rules for Japan depend on your nationality and programme length. Our admissions team explains the options for you.',
  },
  {
    slug: 'seoul',
    name: 'Seoul',
    country: 'South Korea',
    seasonal: seasonalCopy('Seoul', 'seoul', 'Seasonal programmes include camps, mini-stays for school groups and short courses.'),
    image: images.seoul,
    campuses: [{ name: 'Seoul summer centre', lines: ['000 Sample-ro, Floor 00', 'Seoul 00000, South Korea'] }],
    facilities: standardFacilities,
    transport: 'The centre is near several subway stations. Buses also stop close to the building.',
    activities: [
      { name: 'Palace visit', text: 'Explore a historic palace and its gardens.' },
      { name: 'Market food tour', text: 'Try street food and learn how to order in English.' },
      { name: 'River park bike ride', text: 'Ride along the river with your classmates.' },
      { name: 'Dance class', text: 'Learn a popular dance routine with a local teacher.' },
      { name: 'Mountain hike', text: 'Walk a city mountain trail with views over the city.' },
    ],
    visaNote: 'Visa rules for South Korea depend on your nationality and programme length. Our admissions team explains the options for you.',
  },
  {
    slug: 'hong-kong',
    name: 'Hong Kong',
    country: 'Hong Kong SAR',
    seasonal: seasonalCopy('Hong Kong', 'hong kong', 'Seasonal programmes include camps and short English courses.'),
    image: images.hongKong,
    campuses: [{ name: 'Hong Kong summer centre', lines: ['00/F, 000 Sample Road', 'Hong Kong SAR'] }],
    facilities: standardFacilities,
    transport: 'The centre is a short walk from MTR stations. Ferries and buses connect it to the rest of the city.',
    activities: [
      { name: 'Harbour ferry ride', text: 'Cross the harbour and enjoy the city views.' },
      { name: 'Peak walk', text: 'Walk a trail with views over the city and the sea.' },
      { name: 'Night market visit', text: 'Practise English while you shop and try snacks.' },
      { name: 'Island beach day', text: 'Take a ferry to a quiet island beach.' },
      { name: 'Dim sum lunch', text: 'Share a traditional meal with your classmates.' },
    ],
    visaNote: 'Visa rules for Hong Kong depend on your nationality and programme length. Our admissions team explains the options for you.',
  },
  {
    slug: 'cape-town',
    name: 'Cape Town',
    country: 'South Africa',
    seasonal: seasonalCopy('Cape Town', 'cape town', 'Seasonal programmes include camps, mini-stays and short English courses.'),
    image: images.capeTown,
    campuses: [{ name: 'Cape Town summer centre', lines: ['000 Sample Road, Floor 00', 'Cape Town 0000, South Africa'] }],
    facilities: standardFacilities,
    transport: 'Bus routes stop close to the centre. Group leaders travel with young learners on every trip.',
    activities: [
      { name: 'Guided mountain hike', text: 'Walk up a famous mountain with a trained guide.' },
      { name: 'Waterfront visit', text: 'Explore shops, cafés and the harbour.' },
      { name: 'Beach afternoon', text: 'Relax by the sea with your classmates.' },
      { name: 'Botanical garden visit', text: 'Walk through gardens with local plants and flowers.' },
      { name: 'Cultural walking tour', text: 'Learn about local history with a guide.' },
    ],
    visaNote: 'Visa rules for South Africa depend on your nationality and programme length. Our admissions team explains the options for you.',
  },
];
